import { Button, Input } from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import { ColumnDef } from "@tanstack/react-table";
import { Inbox, MessageSquare, Star, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import {
  type AdminFeedbackRow,
  type FeedbackKind,
  type FeedbackStatus,
  type RatingBucket,
  useAdminFeedbackKpis,
  useAdminFeedbackList,
} from "@/admin/services";

import { AdminDataTable } from "../../components/ui/data-table";
import { EmptyState } from "../_components/empty-state";
import { PageHeader } from "../_components/page-header";

const STATUS_LABELS: Record<FeedbackStatus, string> = {
  NEW: "New",
  READ: "Read",
  REPLIED: "Replied",
  RESOLVED: "Resolved",
  ARCHIVED: "Archived",
};

const KIND_LABELS: Record<FeedbackKind, string> = {
  GENERAL: "Idea / Praise",
  NPS_FOLLOWUP: "NPS",
  EXIT_SURVEY: "Exit",
  BUG_REPORT: "Bug",
};

const STATUS_CLASSES: Record<FeedbackStatus, string> = {
  NEW: "bg-primary-soft text-primary",
  READ: "bg-surface text-muted-foreground",
  REPLIED: "bg-success-soft text-success-foreground",
  RESOLVED: "bg-success-soft text-success-foreground",
  ARCHIVED: "bg-surface text-subtle",
};

const KIND_CLASSES: Record<FeedbackKind, string> = {
  GENERAL: "bg-surface text-foreground",
  NPS_FOLLOWUP: "bg-primary-soft text-primary",
  EXIT_SURVEY: "bg-warning-soft text-warning-foreground",
  BUG_REPORT: "bg-danger-soft text-danger-foreground",
};

const StatusBadge = ({ status }: { status: FeedbackStatus }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]",
      STATUS_CLASSES[status],
    )}
  >
    {STATUS_LABELS[status]}
  </span>
);

const KindBadge = ({ kind }: { kind: FeedbackKind }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]",
      KIND_CLASSES[kind],
    )}
  >
    {KIND_LABELS[kind]}
  </span>
);

const RatingCell = ({ rating, kind }: { rating: number | null; kind: FeedbackKind }) => {
  if (rating === null) return <span className="text-subtle">—</span>;
  const isNps = kind === "NPS_FOLLOWUP";
  const tone = isNps
    ? rating >= 9
      ? "text-success-foreground"
      : rating <= 6
        ? "text-danger-foreground"
        : "text-muted-foreground"
    : rating >= 4
      ? "text-success-foreground"
      : rating <= 2
        ? "text-danger-foreground"
        : "text-muted-foreground";
  return (
    <div className={cn("flex items-center gap-1.5 tabular-nums", tone)}>
      <Star className={cn("size-3.5", rating >= (isNps ? 9 : 4) ? "fill-current" : "")} />
      <span className="text-[12px] font-semibold">{rating}</span>
      {!isNps && <span className="text-[10px] text-subtle">/5</span>}
    </div>
  );
};

const PreviewCell = ({ row }: { row: AdminFeedbackRow }) => (
  <div className="flex max-w-[420px] flex-col gap-1">
    <span className="line-clamp-2 text-[13px] text-foreground">{row.message}</span>
    <span className="mono text-[11px] text-subtle">
      {row.surface}
      {row.tags.length > 0 && (
        <>
          <span className="px-1 opacity-50">·</span>
          {row.tags.slice(0, 2).join(", ")}
          {row.tags.length > 2 && <span className="text-subtle">+{row.tags.length - 2}</span>}
        </>
      )}
    </span>
  </div>
);

const KpiTile = ({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "default" | "warning" | "success";
}) => {
  const toneClass =
    tone === "warning"
      ? "text-warning-foreground"
      : tone === "success"
        ? "text-success-foreground"
        : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">{label}</p>
      <p className={cn("mt-1.5 text-[22px] font-semibold tabular-nums leading-none", toneClass)}>
        {value}
      </p>
      {hint && <p className="mt-1.5 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
};

type StatusFilter = FeedbackStatus | "all";
type KindFilter = FeedbackKind | "all";
type RatingFilter = RatingBucket | "all";

export const AdminFeedbackPage = () => {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>("all");
  const [search, setSearch] = useState("");

  const query = useMemo(
    () => ({
      status: statusFilter === "all" ? undefined : statusFilter,
      kind: kindFilter === "all" ? undefined : kindFilter,
      ratingBucket: ratingFilter === "all" ? undefined : ratingFilter,
      search: search.trim().length >= 2 ? search.trim() : undefined,
      take: 100,
    }),
    [statusFilter, kindFilter, ratingFilter, search],
  );

  const { data, isLoading } = useAdminFeedbackList(query);
  const { data: kpis } = useAdminFeedbackKpis();

  const columns = useMemo<ColumnDef<AdminFeedbackRow>[]>(
    () => [
      {
        accessorKey: "createdAt",
        header: "When",
        cell: ({ row }) => (
          <span className="text-[12px] tabular-nums text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString()}{" "}
            <span className="text-subtle">
              {new Date(row.original.createdAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </span>
        ),
      },
      {
        accessorKey: "kind",
        header: "Type",
        cell: ({ row }) => <KindBadge kind={row.original.kind} />,
      },
      {
        accessorKey: "rating",
        header: "Score",
        cell: ({ row }) => <RatingCell rating={row.original.rating} kind={row.original.kind} />,
      },
      {
        id: "message",
        header: "Message",
        cell: ({ row }) => <PreviewCell row={row.original} />,
      },
      {
        id: "from",
        header: "From",
        cell: ({ row }) => {
          const display =
            row.original.user?.name ??
            row.original.user?.email ??
            row.original.email ??
            "Anonymous";
          const sub =
            row.original.user?.email && row.original.user.email !== display
              ? row.original.user.email
              : null;
          return (
            <div className="flex flex-col text-[12px]">
              <span className="font-medium text-foreground">{display}</span>
              {sub && <span className="text-subtle">{sub}</span>}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
    ],
    [],
  );

  const totalLabel = data?.total ? `${data.total.toLocaleString()} entries.` : undefined;
  const filtersActive =
    statusFilter !== "all" ||
    kindFilter !== "all" ||
    ratingFilter !== "all" ||
    search.trim().length > 0;

  return (
    <>
      <Helmet>
        <title>Feedback · Admin</title>
      </Helmet>

      <PageHeader
        title="Feedback"
        description={`Triage NPS, CSAT, exit surveys, and bug reports. ${totalLabel ?? ""}`}
        trail={["Admin"]}
      />

      {/* Tiles focus on admin inbox workload. Closed-ended NPS/CSAT score
          analytics live in PostHog where every survey response is captured. */}
      {kpis && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiTile label="New" value={kpis.newCount} hint="Awaiting triage" />
          <KpiTile
            label="Detractor follow-ups (30d)"
            value={kpis.detractorFollowupCount30d}
            hint="NPS comments"
            tone={kpis.detractorFollowupCount30d > 0 ? "warning" : "default"}
          />
          <KpiTile
            label="Exit feedback (30d)"
            value={kpis.exitSurveyCount30d}
            hint="Pre-portal reasons"
            tone={kpis.exitSurveyCount30d > 0 ? "warning" : "default"}
          />
          <KpiTile
            label="> 48h unread"
            value={kpis.unansweredOlderThan48hCount}
            hint="NEW or READ"
            tone={kpis.unansweredOlderThan48hCount > 0 ? "warning" : "success"}
          />
        </div>
      )}

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <FilterPillRow
          label="Status"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as StatusFilter)}
          options={[
            { value: "all", label: "All" },
            { value: "NEW", label: "New" },
            { value: "READ", label: "Read" },
            { value: "REPLIED", label: "Replied" },
            { value: "RESOLVED", label: "Resolved" },
            { value: "ARCHIVED", label: "Archived" },
          ]}
        />
        <FilterPillRow
          label="Type"
          value={kindFilter}
          onChange={(v) => setKindFilter(v as KindFilter)}
          options={[
            { value: "all", label: "All" },
            { value: "GENERAL", label: "Idea/Praise" },
            { value: "NPS_FOLLOWUP", label: "NPS" },
            { value: "EXIT_SURVEY", label: "Exit" },
            { value: "BUG_REPORT", label: "Bug" },
          ]}
        />
        <FilterPillRow
          label="Score"
          value={ratingFilter}
          onChange={(v) => setRatingFilter(v as RatingFilter)}
          options={[
            { value: "all", label: "All" },
            { value: "detractor", label: "NPS 0-6" },
            { value: "passive", label: "NPS 7-8" },
            { value: "promoter", label: "NPS 9-10" },
            { value: "low", label: "CSAT ≤2" },
            { value: "high", label: "CSAT ≥4" },
          ]}
        />
        <div className="relative ml-auto w-full sm:w-72">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search messages, emails…"
            className="h-8 border-border bg-card pr-8 text-[13px]"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-subtle hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      {!isLoading && (data?.rows.length ?? 0) === 0 ? (
        <EmptyState
          icon={filtersActive ? MessageSquare : Inbox}
          title={filtersActive ? "No feedback matches these filters." : "Inbox zero."}
          description={
            filtersActive
              ? "Try widening the filters or clearing the search."
              : "Once users submit through any survey or the always-on widget, entries land here."
          }
          action={
            filtersActive ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setStatusFilter("all");
                  setKindFilter("all");
                  setRatingFilter("all");
                  setSearch("");
                }}
              >
                Clear filters
              </Button>
            ) : null
          }
        />
      ) : (
        <AdminDataTable
          columns={columns}
          data={data?.rows ?? []}
          loading={isLoading}
          pageSize={50}
          onRowClick={(row) => navigate(`/feedback/${row.id}`)}
        />
      )}
    </>
  );
};

const FilterPillRow = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) => (
  <div className="flex items-center gap-2">
    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">
      {label}
    </span>
    <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1">
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "inline-flex items-center rounded-sm px-2 py-0.5 text-[12px] font-medium transition-colors duration-120 ease-out",
              isActive
                ? "bg-surface text-foreground"
                : "text-muted-foreground hover:bg-surface-muted hover:text-foreground",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default AdminFeedbackPage;
